import React, { useState } from 'react';
import Button from '../../../components/base/Button';

export default function LibrariesSection() {
  const [showLibraryModal, setShowLibraryModal] = useState(false);

  const libraryStats = [
    { number: '150+', label: 'Primary Health Centers', icon: 'ri-hospital-line' },
    { number: '500+', label: 'Educational Materials', icon: 'ri-book-2-line' },
    { number: '25', label: 'States Covered', icon: 'ri-map-2-line' },
    { number: '50K+', label: 'Healthcare Workers Trained', icon: 'ri-team-line' }
  ];

  const libraryFeatures = [
    {
      title: 'Comprehensive Medical Literature',
      description: 'Complete collection of oxygen health resources including diagnostic guides, treatment protocols, and prevention strategies.',
      icon: 'ri-book-open-line',
      image: 'https://public.readdy.ai/ai/img_res/df0557aef8b6c63ef6a5a1b19f3e6061.jpg'
    },
    {
      title: 'Digital Access Terminals',
      description: 'Modern computer stations providing access to online resources, video tutorials, and interactive learning modules.',
      icon: 'ri-computer-line',
      image: 'https://readdy.ai/api/search-image?query=Modern%20computer%20terminal%20in%20healthcare%20facility%20showing%20digital%20medical%20education%20platform%2C%20clean%20digital%20library%20setup%20with%20medical%20software%20and%20online%20learning%20resources%20about%20oxygen%20health%2C%20professional%20healthcare%20technology%20setup&width=400&height=300&seq=digital-terminal&orientation=landscape'
    },
    {
      title: 'Multi-language Resources',
      description: 'Educational materials available in local languages to ensure accessibility across diverse rural communities.',
      icon: 'ri-global-line',
      image: 'https://readdy.ai/api/search-image?query=Healthcare%20educational%20materials%20displayed%20in%20multiple%20Indian%20languages%2C%20diverse%20language%20books%20and%20pamphlets%20about%20oxygen%20health%2C%20multilingual%20medical%20education%20resources%20in%20rural%20health%20center%20with%20colorful%20language-specific%20materials&width=400&height=300&seq=multilingual-resources&orientation=landscape'
    },
    {
      title: 'Training Equipment',
      description: 'Hands-on training tools including pulse oximeters, educational models, and demonstration equipment for practical learning.',
      icon: 'ri-stethoscope-line',
      image: 'https://readdy.ai/api/search-image?query=Medical%20training%20equipment%20for%20oxygen%20health%20education%20including%20pulse%20oximeters%20and%20respiratory%20models%2C%20healthcare%20training%20tools%20and%20educational%20medical%20devices%20in%20primary%20health%20center%2C%20professional%20medical%20education%20equipment&width=400&height=300&seq=training-equipment&orientation=landscape'
    }
  ];

  const libraryResources = [
    {
      category: 'Diagnostic Guides',
      items: [
        'Oxygen Saturation Measurement Protocol',
        'Clinical Signs of Hypoxemia Recognition',
        'Pulse Oximetry Best Practices',
        'Emergency Assessment Guidelines'
      ]
    },
    {
      category: 'Treatment Protocols',
      items: [
        'Oxygen Therapy Administration',
        'Patient Monitoring Procedures',
        'Medication Management',
        'Referral Decision Tree'
      ]
    },
    {
      category: 'Educational Materials',
      items: [
        'Community Health Awareness Booklets',
        'Visual Learning Aids',
        'Multi-language Resources',
        'Interactive Training Modules'
      ]
    },
    {
      category: 'Research & Data',
      items: [
        'Regional Health Statistics',
        'Case Study Collections',
        'Best Practice Documentation',
        'Impact Assessment Reports'
      ]
    }
  ];

  return (
    <section className="py-20 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-6">
            Oxygen Libraries in PHCs
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            Establishing dedicated oxygen health libraries in Primary Health Centers 
            to ensure healthcare workers have immediate access to critical educational resources.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {libraryStats.map((stat, index) => (
            <div key={index} className="text-center bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow duration-300">
              <div className="w-16 h-16 bg-[#12B6B6]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className={`${stat.icon} text-2xl text-[#12B6B6]`}></i>
              </div>
              <div className="text-3xl font-bold text-[#111111] mb-2">{stat.number}</div>
              <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {libraryFeatures.map((feature, index) => (
            <div 
              key={index}
              className="group bg-white rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src={feature.image} 
                  alt={feature.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-[#12B6B6]/10 rounded-full flex items-center justify-center">
                    <i className={`${feature.icon} text-xl text-[#12B6B6]`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#111111]">{feature.title}</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Implementation Process */}
        <div className="bg-white rounded-2xl p-8 md:p-12">
          <h3 className="text-3xl font-bold text-[#111111] text-center mb-12">
            Library Implementation Process
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#12B6B6] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                1
              </div>
              <h4 className="font-bold text-[#111111] mb-2">Assessment</h4>
              <p className="text-sm text-gray-600">Evaluate PHC needs and existing infrastructure</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#12B6B6] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                2
              </div>
              <h4 className="font-bold text-[#111111] mb-2">Setup</h4>
              <p className="text-sm text-gray-600">Install library infrastructure and digital systems</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#12B6B6] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                3
              </div>
              <h4 className="font-bold text-[#111111] mb-2">Training</h4>
              <p className="text-sm text-gray-600">Train healthcare staff on resource utilization</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#12B6B6] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                4
              </div>
              <h4 className="font-bold text-[#111111] mb-2">Monitor</h4>
              <p className="text-sm text-gray-600">Ongoing support and resource updates</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Button 
            variant="outline" 
            size="lg"
            onClick={() => setShowLibraryModal(true)}
          >
            Browse Library
          </Button>
        </div>
      </div>

      {/* Library Browse Modal */}
      {showLibraryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl relative z-[100000]">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-[#111111]">Oxygen Health Library Resources</h3>
              <button
                onClick={() => setShowLibraryModal(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-600"></i>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {libraryResources.map((category, index) => (
                  <div key={index} className="bg-[#FAFAFA] rounded-xl p-6">
                    <h4 className="text-lg font-bold text-[#111111] mb-4 flex items-center space-x-2">
                      <div className="w-8 h-8 bg-[#12B6B6]/10 rounded-full flex items-center justify-center">
                        <i className="ri-folder-line text-sm text-[#12B6B6]"></i>
                      </div>
                      <span>{category.category}</span>
                    </h4>
                    <ul className="space-y-3">
                      {category.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start space-x-3 cursor-pointer hover:text-[#12B6B6] transition-colors">
                          <i className="ri-file-text-line w-4 h-4 flex items-center justify-center text-[#12B6B6] mt-0.5"></i>
                          <span className="text-sm text-gray-700 hover:text-[#12B6B6]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 p-6 bg-gradient-to-r from-[#12B6B6] to-[#0ea5a5] rounded-xl text-white text-center">
                <i className="ri-information-line text-2xl mb-2"></i>
                <h4 className="font-bold mb-2">Access Full Library</h4>
                <p className="text-sm opacity-90 mb-4">
                  Complete digital library access is available at participating Primary Health Centers. 
                  Contact your local PHC for hands-on access to all resources.
                </p>
                <Button 
                  variant="secondary"
                  onClick={() => setShowLibraryModal(false)}
                  className="bg-white text-[#12B6B6] hover:bg-gray-100"
                >
                  Request Library Setup
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
